/**
 * IconMenu - Simple dropdown menu with customizable button
 *
 * Allows custom button component or uses default icon button
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  CellMenuItem,
  usePopover,
} from '@client/new/components/TableShared/Cell';
import type { IMenuItem } from '@client/new/components/TableShared/Cell';
import React from 'react';
import { F1 } from '@client/new/helpers/types';

type IconMenuProps = {
  icon?: IconProp;
  items: IMenuItem[];
  buttonComponent?: React.ComponentType<{
    isOpen: boolean;
    onClick: F1<React.MouseEvent>;
  }>;
};

export const IconMenu = (p: IconMenuProps) => {
  const popover = usePopover<HTMLDivElement>();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    popover.handleOpen();
  };

  const ButtonComponent = p.buttonComponent;

  return (
    <>
      <div ref={popover.anchorRef}>
        {ButtonComponent ? (
          <ButtonComponent isOpen={popover.open} onClick={handleClick} />
        ) : (
          <button
            style={{
              zIndex: 10,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
            onClick={handleClick}
          >
            {p.icon && (
              <FontAwesomeIcon
                icon={p.icon}
                className="text-gray-600 hover:text-gray-800 text-[16px]"
              />
            )}
          </button>
        )}
      </div>
      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disableScrollLock
        open={popover.open}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClose={(e: any) => {
          popover.handleClose();
          e?.stopPropagation();
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root > .MuiMenu-list': {
            padding: 0,
          },
        }}
      >
        {p.items.map((item, index) => (
          <CellMenuItem key={'item' + index} item={item} popover={popover} />
        ))}
      </Menu>
    </>
  );
};
